import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Link, useNavigate } from '@tanstack/react-router';
import { Card, SortableHeader, Table, TableColumnsMenu, TextLink, useTableColumns, } from '@wit-support/ui';
import { gapMinor, moneyWhole, num2, roas } from '../format.js';
import { campaignTitle, splitCommonPrefix } from '../campaign-name.js';
import { columnHint } from '../glossary.js';
import { menuSort, paybackColumnsKey } from '../table-columns.js';
import { sortBy } from '../table-sort.js';
import { linkTo, useTableUrl } from '../url-state.js';
import { CppCell, GapCell } from './money-cells.js';
export const ATTENTION_SORT_KEYS = ['name', 'spend', 'cpp', 'roas', 'hi', 'gap'];
const ATTENTION_COLUMN_LABEL = {
    name: 'Кампания',
    spend: 'Расход',
    cpp: 'Цена покупки',
    roas: 'Вернётся на 1 $',
    hi: 'В лучшем случае',
    gap: 'Не хватает / запас',
};
/** Колонки «Требуют внимания»: «Кампания» закреплена и всегда видна. */
export const ATTENTION_COLUMN_DEFS = ATTENTION_SORT_KEYS.map((id) => ({
    id,
    label: ATTENTION_COLUMN_LABEL[id],
    ...(id === 'name' ? { defaultPin: 'left', hideable: false } : {}),
}));
const CELL_CLASS = {
    name: 'max-w-[440px]',
    spend: 'text-right tabular-nums',
    cpp: 'text-right',
    roas: 'text-right font-semibold tabular-nums',
    hi: 'text-right text-[var(--color-text-muted)] tabular-nums',
    gap: 'text-right',
};
/**
 * «Требуют внимания · не окупаются даже в лучшем случае · 30 дн» (спека §6.1
 * п.5). Клик — Кампании за 30 дней с панелью этой кампании.
 */
export function AttentionTable({ rows, total, theta, period30, search, }) {
    const navigate = useNavigate();
    const t = useTableUrl('att', ATTENTION_SORT_KEYS, { key: 'gap', dir: 'asc' });
    const valueOf = (c, k) => {
        switch (k) {
            case 'name':
                return campaignTitle(c);
            case 'spend':
                return c.spendMinorFull;
            case 'cpp':
                return c.cpp;
            case 'roas':
                return c.answer.roas3;
            case 'hi':
                return c.answer.interval?.hi ?? null;
            case 'gap':
                return gapMinor(c, theta);
        }
    };
    const sorted = sortBy(rows, t.sort, valueOf);
    const named = sorted.map((c) => c.campaignName ?? null);
    const parts = named.every((n) => n !== null) ? splitCommonPrefix(named) : null;
    const all = linkTo('campaigns', { from: period30.from, to: period30.to }, search);
    const cols = useTableColumns(paybackColumnsKey('attention'), ATTENTION_COLUMN_DEFS);
    const cellOf = (k, c, l, p) => {
        switch (k) {
            case 'name': {
                const title = campaignTitle(c);
                return (_jsx(TextLink, { asChild: true, className: "block truncate", children: _jsx(Link, { to: l.to, search: l.search, title: title, onClick: (e) => e.stopPropagation(), children: p && p.dim ? (_jsxs(_Fragment, { children: [_jsx("span", { className: "text-[var(--color-text-muted)]", children: p.dim }), _jsx("b", { children: p.bold })] })) : (_jsx("b", { children: title })) }) }));
            }
            case 'spend':
                return moneyWhole(c.spendMinorFull);
            case 'cpp':
                return _jsx(CppCell, { cpp: c.cpp, breakeven: c.cppBreakevenMinor });
            case 'roas':
                return roas(c.answer.roas3);
            case 'hi':
                return roas(c.answer.interval?.hi ?? null);
            case 'gap':
                return _jsx(GapCell, { gap: gapMinor(c, theta), verdict: c.answer.verdict });
        }
    };
    return (_jsxs(Card, { id: "attention", className: "px-4 pb-1.5 pt-3", "aria-label": "\u0422\u0440\u0435\u0431\u0443\u044E\u0442 \u0432\u043D\u0438\u043C\u0430\u043D\u0438\u044F", children: [_jsxs("div", { className: "flex flex-wrap items-center justify-between gap-2 px-1 pb-1", children: [_jsxs("h2", { className: "text-sm font-semibold", children: ["\u0422\u0440\u0435\u0431\u0443\u044E\u0442 \u0432\u043D\u0438\u043C\u0430\u043D\u0438\u044F", ' ', _jsx("span", { className: "font-normal text-xs text-[var(--color-text-muted)]", children: "\u00B7 \u043D\u0435 \u043E\u043A\u0443\u043F\u0430\u044E\u0442\u0441\u044F \u0434\u0430\u0436\u0435 \u0432 \u043B\u0443\u0447\u0448\u0435\u043C \u0441\u043B\u0443\u0447\u0430\u0435 \u00B7 30 \u0434\u043D" })] }), _jsxs("div", { className: "flex flex-wrap items-center gap-2", children: [_jsx(TextLink, { asChild: true, className: "text-xs", children: _jsxs(Link, { to: all.to, search: all.search, children: ["\u0432\u0441\u0435 ", total, " \u043A\u0430\u043C\u043F\u0430\u043D\u0438\u0439 \u0437\u0430 30 \u0434\u043D \u2192"] }) }), rows.length > 0 ? (_jsx(TableColumnsMenu, { columns: cols, sort: menuSort(t.sort, ATTENTION_COLUMN_DEFS) })) : null] })] }), rows.length === 0 ? (_jsx("p", { className: "px-1 py-3 text-sm text-[var(--color-text-muted)]", children: "\u041D\u0435\u0442 \u043A\u0430\u043C\u043F\u0430\u043D\u0438\u0439, \u043A\u043E\u0442\u043E\u0440\u044B\u0435 \u043D\u0435 \u043E\u043A\u0443\u043F\u0430\u044E\u0442\u0441\u044F \u0434\u0430\u0436\u0435 \u0432 \u043B\u0443\u0447\u0448\u0435\u043C \u0441\u043B\u0443\u0447\u0430\u0435" })) : (_jsxs(Table, { density: "compact", children: [_jsx(Table.Header, { children: _jsx(Table.Row, { children: cols.visible.map((col) => (_jsx(SortableHeader, { column: col, sortKey: col.id, sort: t.sort, onSortChange: t.setSort, align: col.id === 'name' ? 'left' : 'right', defaultDirection: col.id === 'name' || col.id === 'gap' ? 'asc' : 'desc', className: "whitespace-nowrap", title: col.id === 'hi'
                                    ? `Лучший правдоподобный случай — всё равно ниже порога ${num2(theta)}`
                                    : undefined, hint: columnHint(col.id, theta), children: col.def.label }, col.id))) }) }), _jsx(Table.Body, { children: sorted.map((c, i) => {
                            const l = linkTo('campaigns', { from: period30.from, to: period30.to, campaign: c.campaignId }, search);
                            const p = parts?.[i];
                            return (_jsx(Table.Row, { className: "cursor-pointer", "data-testid": "attention-row", onClick: () => void navigate({ to: l.to, search: l.search }), children: cols.visible.map((col) => (_jsx(Table.Cell, { column: col, className: CELL_CLASS[col.id], children: cellOf(col.id, c, l, p) }, col.id))) }, c.campaignId));
                        }) })] }))] }));
}
//# sourceMappingURL=attention-table.js.map